import React, { useState, useEffect, useRef } from 'react';
import { VolumeX, Volume2, Play, ArrowRight, X } from 'lucide-react';
import { storytellingChapters } from '../data/fallbackData';
import { getOptimizedImageUrl } from '../lib/utils';

export default function Storytelling() {
  const [chapters, setChapters] = useState(storytellingChapters);
  const sectionRef = useRef(null);
  const trackRef = useRef(null);
  const videoRefs = useRef({});
  
  const [scrollProgress, setScrollProgress] = useState(0);
  const [maxTranslatePx, setMaxTranslatePx] = useState(0);
  const [activeChapterIndex, setActiveChapterIndex] = useState(0);
  const [mutedStates, setMutedStates] = useState({});
  const [playingStates, setPlayingStates] = useState({});
  const [activeVideoChapter, setActiveVideoChapter] = useState(null);

  useEffect(() => {
    async function fetchChapters() {
      try {
        const res = await fetch('/_api/public/v2/content/brandfilm?sort=sortering&order=asc');
        if (res.ok) {
          const json = await res.json();
          if (json?.items && json.items.length > 0) {
            const mapped = json.items.map((item, idx) => {
              const isVideo = item.terms?.['medie-type']?.includes('video') || Boolean(item.video_url);
              const imgUrl = Array.isArray(item.billede) ? item.billede[0] : item.billede;
              return {
                number: item.kapitel_nummer || `0${idx + 1}`,
                type: isVideo ? 'video' : 'image',
                src: isVideo ? (item.video_url || imgUrl) : imgUrl,
                tagline: item.tagline || '',
                title: item.titel || '',
                subtitle: item.undertitel || '',
                body: item.beskrivelse || '',
                accent: item.accent_tekst || '',
                duration: item.format_tekst || (isVideo ? 'Video' : 'Fotografi'),
              };
            });
            setChapters(mapped);
          }
        }
      } catch (err) {
        console.warn('Using fallback storytelling chapters:', err);
      }
    }
    fetchChapters();
  }, []);

  // Handle escape key to close video lightbox
  useEffect(() => {
    if (!activeVideoChapter) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setActiveVideoChapter(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [activeVideoChapter]);

  // Recalculate track metrics
  const updateMetrics = () => {
    if (!trackRef.current) return;
    const trackWidth = trackRef.current.scrollWidth;
    const viewportWidth = window.innerWidth;
    const maxScroll = Math.max(0, trackWidth - viewportWidth + 64);
    setMaxTranslatePx(maxScroll);
  };

  useEffect(() => {
    updateMetrics();
    window.addEventListener('resize', updateMetrics);
    return () => window.removeEventListener('resize', updateMetrics);
  }, []);

  // Track vertical page scroll and translate to horizontal progression
  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const totalScrollable = Math.max(1, rect.height - window.innerHeight);
      const scrolled = Math.min(totalScrollable, Math.max(0, -rect.top));
      const progress = Math.min(1, Math.max(0, scrolled / totalScrollable));

      setScrollProgress(progress);

      const totalChapters = chapters.length;
      const calculatedIndex = Math.min(
        totalChapters - 1,
        Math.floor(progress * totalChapters)
      );
      setActiveChapterIndex(calculatedIndex);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [chapters.length]);

  const scrollToChapter = (index) => {
    if (!sectionRef.current) return;
    const totalChapters = chapters.length;
    const targetProgress = index / (totalChapters - 1);
    const rect = sectionRef.current.getBoundingClientRect();
    const currentScrollY = window.scrollY;
    const sectionTop = currentScrollY + rect.top;
    const totalScrollable = Math.max(1, rect.height - window.innerHeight);
    const targetScrollY = sectionTop + targetProgress * totalScrollable;

    window.scrollTo({
      top: targetScrollY,
      behavior: 'smooth',
    });
  };

  const handleToggleMute = (idx, e) => {
    e.stopPropagation();
    const video = videoRefs.current[idx];
    if (!video) return;
    const currentMuted = mutedStates[idx] ?? true;
    video.muted = !currentMuted;
    video.volume = 0.1;
    setMutedStates((prev) => ({ ...prev, [idx]: !currentMuted }));
  };

  const currentTranslateX = scrollProgress * maxTranslatePx;

  return (
    <section
      id="story"
      ref={sectionRef}
      className="relative w-full bg-[#F2EEE7] min-h-[400vh] text-[#37332F]"
      aria-label="Brandfilm og scroll storytelling"
    >
      {/* Sticky Fullscreen Rail Frame */}
      <div className="sticky top-0 h-screen w-full overflow-hidden flex flex-col justify-between pt-24 sm:pt-28 pb-4 sm:pb-6 bg-[#F2EEE7]">
        {/* Subtle decorative ambient warmth */}
        <div className="absolute top-1/3 -left-32 w-96 h-96 bg-[#DED7CC]/40 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-1/3 -right-32 w-96 h-96 bg-[#C6D3D4]/30 rounded-full blur-3xl pointer-events-none" />

        {/* Section Header with Refined Graphic Typography */}
        <div className="relative z-20 px-6 sm:px-12 w-full max-w-7xl mx-auto flex items-end justify-between gap-4 shrink-0">
          <div>
            <span className="font-['Montserrat'] text-[11px] uppercase tracking-[0.3em] text-[#C99850] block mb-1">
              Film & Fortælling
            </span>
            <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-5xl md:text-6xl font-light uppercase tracking-[0.2em] text-[#37332F] leading-none">
              Brandfilm
            </h2>
          </div>

          {/* Quick Chapter Progress Indicators */}
          <div className="hidden sm:flex items-center gap-3">
            <span className="font-['Montserrat'] text-xs text-[#5E5852] tracking-wider">
              {String(activeChapterIndex + 1).padStart(2, '0')} / {String(chapters.length).padStart(2, '0')}
            </span>
            <div className="w-24 h-[2px] bg-[#DED7CC] overflow-hidden">
              <div
                className="h-full bg-[#C99850] transition-all duration-150"
                style={{ width: `${Math.max(10, scrollProgress * 100)}%` }}
              />
            </div>
          </div>
        </div>

        {/* Horizontal Track with Large Visual Surfaces */}
        <div className="relative z-10 w-full overflow-hidden my-auto py-2 flex-1 flex items-center min-h-0">
          <div
            ref={trackRef}
            style={{
              transform: `translate3d(-${currentTranslateX}px, 0, 0)`,
              willChange: 'transform',
            }}
            className="flex items-stretch gap-6 sm:gap-10 pl-6 sm:pl-12 md:pl-20 pr-16 md:pr-32 transition-transform duration-75 ease-out h-full max-h-[52vh] sm:max-h-[58vh] lg:max-h-[60vh]"
          >
            {chapters.map((chapter, idx) => {
              const isVideo = chapter.type === 'video';
              const isMuted = mutedStates[idx] ?? true;
              const isActive = activeChapterIndex === idx;

              return (
                <article
                  key={chapter.number}
                  onClick={() => scrollToChapter(idx)}
                  className={`shrink-0 w-[85vw] sm:w-[480px] md:w-[540px] lg:w-[600px] flex flex-col justify-between bg-[#F6F2EB] border transition-all duration-500 cursor-pointer group select-none overflow-hidden relative ${
                    isActive
                      ? 'border-[#C99850] shadow-[0_15px_40px_-10px_rgba(55,51,47,0.12)]'
                      : 'border-[#DED7CC] hover:border-[#37332F]/40 opacity-90 hover:opacity-100'
                  }`}
                >
                  {/* Large Visual Surface: Video or Image */}
                  <div className="relative w-full aspect-[16/10] sm:aspect-[16/9] bg-[#EAE5DD] overflow-hidden shrink-0">
                    {isVideo ? (
                      <div className="relative w-full h-full">
                        <video
                          ref={(el) => {
                            videoRefs.current[idx] = el;
                            if (el) el.volume = 0.1;
                          }}
                          src={chapter.src}
                          playsInline
                          loop
                          muted={isMuted}
                          autoPlay
                          preload="metadata"
                          className="w-full h-full object-cover brightness-[0.92] contrast-[0.98] sepia-[0.05] group-hover:scale-102 transition-transform duration-700"
                        />
                        
                        {/* Compact Video Audio Control */}
                        <div className="absolute bottom-3 right-3 flex items-center gap-2 z-20">
                          <button
                            type="button"
                            onClick={(e) => handleToggleMute(idx, e)}
                            className="px-2.5 py-1 bg-[#F6F2EB]/95 hover:bg-[#F6F2EB] border border-[#DED7CC] text-[10px] font-['Montserrat'] text-[#37332F] flex items-center gap-1.5 transition-colors cursor-pointer"
                            aria-label={isMuted ? 'Slå lyd til' : 'Slå lyd fra'}
                          >
                            {isMuted ? (
                              <VolumeX className="w-3.5 h-3.5 text-[#8E867E]" aria-hidden="true" />
                            ) : (
                              <Volume2 className="w-3.5 h-3.5 text-[#C99850]" aria-hidden="true" />
                            )}
                            <span>{isMuted ? 'Lyd fra' : 'Lyd til'}</span>
                          </button>
                        </div>
                      </div>
                    ) : (
                      <img
                        src={getOptimizedImageUrl(chapter.src, 1200, 85)}
                        alt={chapter.title}
                        className="w-full h-full object-cover brightness-[0.92] contrast-[0.98] sepia-[0.05] group-hover:scale-102 transition-transform duration-700"
                        loading="lazy"
                      />
                    )}

                    {/* Subtle Gradient Overlay for Text Transition */}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#F6F2EB]/90 via-transparent to-transparent pointer-events-none" />

                    {/* Graphic Watermark Number */}
                    <div className="absolute top-2 right-4 font-['Cormorant_Garamond'] text-5xl sm:text-6xl font-light text-[#37332F]/10 select-none pointer-events-none">
                      {chapter.number}
                    </div>
                  </div>

                  {/* Editorial Card Content */}
                  <div className="p-4 sm:p-6 flex flex-col justify-between flex-1 space-y-2">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-['Montserrat'] font-medium text-[#C99850]">
                          {chapter.number}
                        </span>
                        <span className="w-4 h-px bg-[#DED7CC]" />
                        <span className="text-[10px] uppercase tracking-[0.25em] text-[#8E867E] font-medium">
                          {chapter.tagline}
                        </span>
                      </div>

                      <h3 className="font-['Cormorant_Garamond'] text-xl sm:text-2xl font-normal uppercase tracking-[0.12em] text-[#37332F] group-hover:text-[#C99850] transition-colors leading-tight">
                        {chapter.title}
                      </h3>

                      <p className="text-xs sm:text-sm text-[#5E5852] font-light leading-relaxed mt-1 line-clamp-2">
                        {chapter.body}
                      </p>
                    </div>

                    {/* Card Footer with Video Lightbox CTA */}
                    <div className="pt-2 border-t border-[#DED7CC] flex items-center justify-between">
                      <span className="text-[10px] font-['Montserrat'] text-[#8E867E] tracking-wider uppercase">
                        {chapter.accent}
                      </span>
                      {isVideo && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveVideoChapter(chapter);
                          }}
                          className="text-[11px] text-[#37332F] hover:text-[#C99850] font-['Montserrat'] uppercase tracking-wider flex items-center gap-1.5 px-3 py-1 bg-[#EAE5DD]/60 hover:bg-[#EAE5DD] border border-[#DED7CC] transition-all cursor-pointer"
                          aria-label={`Åbn video ${chapter.title}`}
                        >
                          <Play className="w-2.5 h-2.5 fill-[#C99850] text-[#C99850]" aria-hidden="true" />
                          <span>Fuldskærm</span>
                        </button>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>

        {/* Bottom Horizontal Scroll Cue */}
        <div className="relative z-20 px-6 sm:px-12 w-full max-w-7xl mx-auto flex items-center justify-between text-xs font-['Montserrat'] text-[#8E867E]">
          <div className="flex items-center gap-2">
            <ArrowRight className="w-3.5 h-3.5 text-[#C99850]" aria-hidden="true" />
            <span className="uppercase text-[10px] tracking-widest">Scroll videre for at udforske</span>
          </div>
        </div>

        {/* Seamless edge blend into The Experience */}
        <div className="absolute bottom-0 inset-x-0 h-16 bg-gradient-to-t from-[#F6F2EB] to-transparent pointer-events-none z-10" />
      </div>

      {/* Fullscreen Video Modal */}
      {activeVideoChapter && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`Video: ${activeVideoChapter.title}`}
          className="fixed inset-0 z-50 flex items-center justify-center bg-[#2B2724]/95 backdrop-blur-xl p-4 md:p-8"
          onClick={() => setActiveVideoChapter(null)}
        >
          <button
            type="button"
            onClick={() => setActiveVideoChapter(null)}
            aria-label="Luk videoafspiller"
            className="absolute top-6 right-6 z-50 p-3 text-[#F6F2EB] hover:text-[#C99850] border border-[#F6F2EB]/30 bg-[#37332F]/80 backdrop-blur-md cursor-pointer"
          >
            <X className="w-5 h-5" aria-hidden="true" />
          </button>

          <div
            className="relative max-w-5xl w-full flex flex-col items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative w-full aspect-video bg-black overflow-hidden border border-[#DED7CC]/30 shadow-2xl">
              <video
                key={activeVideoChapter.src}
                src={activeVideoChapter.src}
                controls
                autoPlay
                playsInline
                ref={(el) => {
                  if (el) el.volume = 0.1;
                }}
                className="w-full h-full object-contain"
              />
            </div>
            
            <div className="mt-4 w-full flex items-center justify-between text-left">
              <div>
                <h3 className="font-['Cormorant_Garamond'] text-2xl font-light uppercase tracking-wider text-[#F6F2EB]">
                  {activeVideoChapter.title}
                </h3>
                <p className="text-xs font-['Montserrat'] text-[#DED7CC]">
                  {activeVideoChapter.subtitle}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
